/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Gets container rectangle for scroll calculation.
 * 
 * @param {HTMLElement | Document} scrollableContainer - Container
 * @param {boolean} isDocument - Whether container is document
 * @returns {DOMRect | {top: number, left: number}} Container rectangle
 */
export function getContainerRect(
  scrollableContainer: HTMLElement | Document,
  isDocument: boolean
): DOMRect | { top: number; left: number } {
  if (isDocument) {
    return { top: 0, left: 0 };
  }
  return (scrollableContainer as HTMLElement).getBoundingClientRect();
}

/**
 * Gets current scroll top position.
 * 
 * @param {HTMLElement | Document} scrollableContainer - Container
 * @param {boolean} isDocument - Whether container is document
 * @returns {number} Scroll top position
 */
export function getScrollTop(
  scrollableContainer: HTMLElement | Document,
  isDocument: boolean
): number {
  if (isDocument) {
    return window.pageYOffset || document.documentElement.scrollTop;
  }
  return (scrollableContainer as HTMLElement).scrollTop;
}

/**
 * Calculates scroll position for an element.
 * 
 * @param {HTMLElement} element - Element to scroll to
 * @param {HTMLElement | Document} scrollableContainer - Container to scroll
 * @returns {number} Target scroll position
 */
export function calculateScrollPosition(
  element: HTMLElement,
  scrollableContainer: HTMLElement | Document
): number {
  const elementRect = element.getBoundingClientRect();
  const isDocument = scrollableContainer === document.documentElement;
  const containerRect = getContainerRect(scrollableContainer, isDocument);
  const scrollTop = getScrollTop(scrollableContainer, isDocument);
  
  return scrollTop + elementRect.top - containerRect.top - 100;
}
